import React, { useCallback } from "react";
import "./Orders.css"; // Ensure this file exists and contains relevant styles
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Info } from "lucide-react";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useOrders } from "../../customHooks/Orders/useOrders";
import { formatLabel } from "../../components/Constants/Constants";
import Pagination from "../../components/Pagination/Pagination";
import CopyIcon from "../../components/CopyIcon/CopyIcon";
import AppLoader from "../../components/AppLoader/appLoader";

// --- Component-Agnostic Helper Data ---

const columnInfo = {
  networkOrderId: "The Network Order ID for the order.",
  sellerOrderName: "The Seller Name associated with the order (Shopify Name).",
  customer:
    "The name and the phone number of the customer who placed the order.",
  amount:
    "The total order value / expected settlement amount / actual settled amount.",
  orderStatus:
    "The current status of the order (e.g., Created, Accepted, Completed).",
  settlementStatus:
    "The current settlement status of the order (e.g., TO_BE_INITIATED, PENDING, SETTLED).",
  createdAt: "The date and time when the order was created.",
  updatedAt: "The date and time when the order was last modified.",
  issues: "The open/closed issues associated with the order. (Note: Red - Open Issues, Gray - Closed Issues, Hover to see counts)",
  domain: "The ONDC domain of the order (e.g., Fashion, Grocery).",
  buyerNpName: "The Buyer NP Name (similar to Buyer App ID).",
  logisticsProvider:
    "The name of the logistics provider handling the order delivery.",
  customer_state: "The current state of the order.",
};

// Array of fields available for the combined search box
const searchableFields = [
  { value: "network_order_id", label: "Network Order ID" },
  { value: "seller_np_order_id", label: "Shopify Name" },
  { value: "customer_name", label: "Customer Name" },
  { value: "customer_mobile", label: "Customer Mobile" },
];

// Helper function to safely extract customer name
const getCustomerName = (order) =>
  order.customer_info?.location?.address?.name || "N/A";
// Helper function to safely extract customer phone
const getCustomerPhone = (order) => order.customer_info?.contact?.phone || "";
// Helper function to safely extract state
const getOrderState = (order) =>
  order.customer_info?.location?.address?.state ||
  "N/A";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    // FIX: Replace space with T to make it ISO compatible for all browsers
    // "2026-01-09 15:07:40" -> "2026-01-09T15:07:40"
    const safeDateString = dateString.replace(" ", "T");

    // Output: Jan 09, 2026 3:07 PM
    return format(new Date(safeDateString), "MMM dd, yyyy h:mm a");
  } catch (error) {
    //console.error("Date Error:", error);
    return dateString; // Return full string if error, don't chop it
  }
};

// --- Orders Component ---

const Orders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const today = new Date().toISOString().slice(0, 10);

  const {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
  } = useOrders();

  // Memoize filter change
  const handleFilterChange = useCallback(
    (key, value) => {
      setFilters((f) => ({
        ...f,
        [key]: value,
      }));
      if (
        key !== "orders_per_page" &&
        key !== "sort_by" &&
        key !== "sort_direction"
      ) {
        setCurrentPage(1);
      }
    },
    [setFilters, setCurrentPage]
  );

  // Apply filters from navigation if provided, then clear them
  React.useEffect(() => {
    const selectedState = location.state?.selectedState;
    const selectedStatus = location.state?.selectedStatus;
    const selectedBapId = location.state?.selectedBapId;

    if (selectedState) {
      handleFilterChange("customer_state", selectedState);
    }

    if (selectedStatus) {
      handleFilterChange("order_status", selectedStatus);
    }

    if (selectedBapId) {
      handleFilterChange("bap_id", selectedBapId);
    }

    if (selectedState || selectedStatus || selectedBapId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, handleFilterChange, navigate]);

  // Handle logic for table header sorting.
  const handleSort = useCallback(
    (sortBy) => {
      const newDirection =
        filters.sort_by === sortBy
          ? filters.sort_direction === "ASC"
            ? "DESC"
            : "ASC"
          : "DESC";

      handleFilterChange("sort_by", sortBy);
      handleFilterChange("sort_direction", newDirection);
    },
    [filters.sort_by, filters.sort_direction, handleFilterChange]
  );

  // Handles changes to the generic search box.
  const handleGenericSearchChange = useCallback(
    (key, value) => {
      setFilters((f) => ({
        ...f,
        search_by: key,
        search_value: value,
      }));
      setCurrentPage(1);
    },
    [setFilters, setCurrentPage]
  );

  // Resets all filters to their initial state.
  const handleClearFilters = useCallback(() => {
    setFilters({
      start_date: "",
      end_date: "",
      order_status: "",
      bap_id: "",
      domain: "",
      seller_np_order_id: "",
      network_order_id: "",
      customer_mobile: "",
      customer_email: "",
      customer_name: "",
      settlement_status: "", // WARNING: Typo maintained
      orders_per_page: "10",
      sort_by: "createdAt",
      sort_direction: "DESC",
      search_by: "",
      search_value: "",
      customer_state:"",
    });
    setCurrentPage(1);
  }, [setFilters, setCurrentPage]);

  // Helper render function for sort icon
  const renderSortIcon = (key) => {
    if (filters.sort_by === key) {
      return filters.sort_direction === "ASC" ? (
        <FaSortUp size={14} className="ms-1" />
      ) : (
        <FaSortDown size={14} className="ms-1" />
      );
    }
    return null;
  };

  // Helper render function for column header with tooltip
  const renderHeader = (label, columnKey, sortByField = null) => {
    const isSortable = !!sortByField;
    return (
      <th
        key={columnKey}
        className={`text-nowrap ${
          isSortable ? "cursor-pointer hover:bg-gray-100" : ""
        }`}
        onClick={isSortable ? () => handleSort(sortByField) : undefined}
      >
        <div className="d-flex justify-content-center align-items-center">
          <span className="me-1">{label}</span>
          {isSortable && renderSortIcon(sortByField)}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-${columnKey}`}>
                {columnInfo[columnKey]}
              </Tooltip>
            }
          >
            <span style={{ cursor: "help" }} className="text-muted">
              <Info size={14} />
            </span>
          </OverlayTrigger>
        </div>
      </th>
    );
  };

  return (
    <>
      <div className="m-4">
        {/* --- Descriptive Text --- */}
        <p className="text-muted small mb-3">
          This page provides a consolidated view of all recorded orders. Use the
          filters below to search for specific orders and click on a Network
          Order ID to view detailed information.
        </p>

        <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
          <div className="row gx-4 gy-3 align-items-end flex-wrap">
            {/* Search Section - Same UI as first snippet */}
            <div className="col-auto">
              <label className="form-label mb-1">Search By</label>
              <div className="input-group" style={{ minWidth: "400px" }}>
                <select
                  className="form-select"
                  value={filters.search_by}
                  onChange={(e) =>
                    handleGenericSearchChange(e.target.value, "")
                  }
                  style={{ maxWidth: "150px" }}
                >
                  <option value="">Select Field</option>
                  {searchableFields.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  className="form-control"
                  placeholder={`Enter ${
                    filters.search_by
                      ? filters.search_by
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())
                      : "value"
                  }`}
                  value={filters.search_value || ""}
                  onChange={(e) =>
                    handleGenericSearchChange(filters.search_by, e.target.value)
                  }
                  disabled={!filters.search_by}
                  style={{
                    fontSize: "12px",
                    padding: "none",
                  }}
                />
              </div>
            </div>

            {/* Domain */}
            <div className="col-auto">
              <label className="form-label mb-1">Domain</label>
              <select
                className="form-select"
                value={filters.domain || ""}
                onChange={(e) => handleFilterChange("domain", e.target.value)}
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.domains?.map((d) => (
                  <option key={d} value={d}>
                    {formatLabel(d)}
                  </option>
                ))}
              </select>
            </div>

            {/* Order Status */}
            <div className="col-auto">
              <label className="form-label mb-1">Order Status</label>
              <select
                className="form-select"
                value={filters.order_status || ""}
                onChange={(e) =>
                  handleFilterChange("order_status", e.target.value)
                }
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.order_statuses?.map((s) => (
                  <option key={s} value={s}>
                    {formatLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            {/* Settlement Status */}
            <div className="col-auto">
              <label className="form-label mb-1">Settlement Status</label>
              <select
                className="form-select"
                value={filters.settlement_status || ""}
                onChange={(e) =>
                  handleFilterChange("settlement_status", e.target.value)
                }
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.settlement_statuses?.map((s) => (
                  <option key={s} value={s}>
                    {formatLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            {/* BAP ID */}
            <div className="col-auto">
              <label className="form-label mb-1">Buyer NP ID (BAP)</label>
              <select
                className="form-select"
                value={filters.bap_id || ""}
                onChange={(e) => handleFilterChange("bap_id", e.target.value)}
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.bap_ids?.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Created From */}
            <div className="col-auto">
              <label className="form-label mb-1">Created From</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date || ""}
                onChange={(e) =>
                  handleFilterChange("start_date", e.target.value)
                }
                max={today}
                style={{
                  fontSize: "12px",
                  padding: "none",
                  minWidth: "130px",
                }}
              />
            </div>

            {/* Created To */}
            <div className="col-auto">
              <label className="form-label mb-1">Created To</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date || ""}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                min={filters.start_date || ""}
                max={today}
                style={{
                  fontSize: "12px",
                  padding: "none",
                  minWidth: "130px",
                }}
              />
            </div>

            {/*State Filter*/}
            <div className="col-auto">
              <label className="form-label mb-1">State</label>
              <select
                className="form-select"
                value={filters.customer_state || ""}
                onChange={(e) => handleFilterChange("customer_state", e.target.value)}
                style={{
                  minWidth: "150px",
                }}
              >
              <option value="">All</option>
                {filterOptions?.states?.map((s) => (
                  <option key={s} value={s}>
                    {formatLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            {/* Reset Filters Button */}
            <div className="col-auto d-flex">
              <button
                type="button"
                className="btn btn-primary no-hover-btn"
                style={{ padding: "5px 8px" }}
                onClick={handleClearFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* --- Table Section --- */}
        {loading ? (
          <AppLoader />
        ) : data && data.length > 0 ? (
          <div className="table-responsive mt-4">
            <table className="table table-bordered table-hover align-middle text-center">
              <thead className="table-light text-center">
                <tr>
                  {/* Table Headers */}
                  {renderHeader("Network Order ID", "networkOrderId")}
                  {renderHeader(
                    "Shopify Name",
                    "sellerOrderName",
                    "seller_order_name"
                  )}
                  {renderHeader("Customer", "customer")}
                  {renderHeader("Order Amt/ Expected/ Settled (₹)", "amount")}
                  {renderHeader("Order Status", "orderStatus")}
                  {renderHeader("Settlement Status", "settlementStatus")}
                  {renderHeader("State", "customer_state")}
                  {renderHeader("Last Modified At", "updatedAt")}
                  {renderHeader("Created At", "createdAt", "createdAt")}
                  {renderHeader("Issues", "issues")}
                  {renderHeader("Domain", "domain")}
                  {renderHeader("Buyer NP Name", "buyerNpName")}
                  {renderHeader("Logistics Provider", "logisticsProvider")}
                </tr>
              </thead>

              <tbody>
                {data.map((order, i) => (
                    <tr key={order.networkorderid || i}>
                      {/* Network Order ID (Clickable/Copy) */}
                      <td>
                        <div className="copy-cell d-flex align-items-center justify-content-center">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-order-${i}`}>
                                {order.networkorderid}
                              </Tooltip>
                            }
                          >
                            <button
                              className="btn btn-link btn-sm p-0 text-truncate"
                              onClick={() =>
                                navigate(`/orders/${order.networkorderid}`)
                              }
                              style={{ maxWidth: "150px" }}
                            >
                              {order.networkorderid}
                            </button>
                          </OverlayTrigger>
                          <CopyIcon textToCopy={order.networkorderid} />
                        </div>
                      </td>

                      {/* Shopify Name */}
                      <td>{order.seller_order_name || "N/A"}</td>

                      {/* Customer Info */}
                      <td>
                        <div className="text-sm">
                          <span className="fw-bold">
                            {getCustomerName(order)}
                          </span>
                          {getCustomerPhone(order) && (
                            <div className="text-muted small">
                              ({getCustomerPhone(order)})
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Order Amount / Expected / Settled */}
                      <td className="text-nowrap">
                        <div className="d-flex flex-column align-items-center">
                          <small className="text-muted">
                            Order/Exp/Settled
                          </small>
                          <span>
                            {order.totalordervalue
                              ? `₹${Number(order.totalordervalue).toFixed(2)}`
                              : "--"}
                            /
                            {order.amounttobesettled
                              ? `₹${Number(order.amounttobesettled).toFixed(2)}`
                              : "--"}
                            /
                            {order.settledamount
                              ? `₹${Number(order.settledamount).toFixed(2)}`
                              : "--"}
                          </span>
                        </div>
                      </td>

                      {/* Order Status (SIMPLIFIED: Removed color badge) */}
                      <td>{formatLabel(order.orderstatus)}</td>

                      {/* Settlement Status (SIMPLIFIED: Removed color badge) */}
                      <td>{formatLabel(order.settlementstatus)}</td>

                      {/* State  */}
                      <td>{formatLabel(getOrderState(order))}</td>

                      {/* Last Modified At */}
                      <td className="small text-nowrap">
                        {formatDate(
                          order.orderupdatedatetime || order.ordercreatedatetime
                        )}
                      </td>

                      {/* Created At */}
                      <td className="small text-nowrap">
                        {formatDate(order.ordercreatedatetime)}
                      </td>

                      {/* Open / Closed Issues (SIMPLIFIED: Replaced colored badge with simple text/number) */}
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-issues-${i}`}>
                              Open: {order.openissuecount || 0} / Closed:{" "}
                              {order.closedissuecount || 0}
                            </Tooltip>
                          }
                        >
                          <span
                            className={`${
                              (order.openissuecount || 0) > 0
                                ? "text-danger fw-bold"
                                : "text-secondary"
                            }`}
                          >
                            {order.totalissuecount || 0}
                          </span>
                        </OverlayTrigger>
                      </td>

                      {/* Domain */}
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-domain-${i}`}>
                              {order.domain}
                            </Tooltip>
                          }
                        >
                          <span
                            className="text-truncate"
                            style={{
                              maxWidth: "80px",
                              display: "inline-block",
                            }}
                          >
                            {order.domain || "N/A"}
                          </span>
                        </OverlayTrigger>
                      </td>

                      {/* Buyer NP Name (with BAP URI in tooltip and copy) */}
                      <td className="copy-cell">
                        <div className="d-flex align-items-center justify-content-center">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-bap-uri-${i}`}>
                                URI: {order.bap_uri}
                              </Tooltip>
                            }
                          >
                            <span
                              className="text-truncate"
                              style={{ maxWidth: "100px" }}
                            >
                              {order.buyernpname}
                            </span>
                          </OverlayTrigger>
                          <CopyIcon textToCopy={order.buyernpname} />
                        </div>
                      </td>

                      {/* Logistics Provider */}
                      <td>{order.logisticsSellerNPName || "N/A"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#9ca3af",
              fontSize: "14px",
              marginTop: "16px",
            }}
          >
            No Orders Found
          </div>
        )}

        {/* --- Pagination --- */}
        {totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            startItem={(currentPage - 1) * itemsPerPage + 1}
                endItem={Math.min(currentPage * itemsPerPage, totalCount)}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </>
  );
};

export default Orders;
